import { Edit, OpenInNew, WhatsApp } from "@mui/icons-material";
import { Avatar, Box, Button, Chip, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IMarketplace } from "../../../../interfaces/IMarketplace";
import { IIntegracaoWhatsApp } from "../../../../interfaces/IIntegracaoMarketplace";
import { TipoMarketplaceCor, TipoMarketplaceDescricao, TipoMarketplaceTitulo } from "../../../../enum/TipoMarketplace";
import {
    AcoesContainer,
    CabecalhoPaper,
    CapaContainer,
    IdentificacaoContainer,
    ResumoContainer,
    ResumoItem,
    ResumoLabel
} from "../../styles";

const VERDE_WHATSAPP = "#25D366";

interface CabecalhoWhatsAppProps {
    marketplace: IMarketplace;
    /** undefined enquanto a conexão ainda não foi consultada */
    conectado?: boolean;
}

export const CabecalhoWhatsApp = ({ marketplace, conectado }: CabecalhoWhatsAppProps) => {
    const navigate = useNavigate();
    const theme = useTheme();

    const integracao = marketplace?.configuracao?.integracao as IIntegracaoWhatsApp | undefined;

    const corStatus = conectado === undefined
        ? theme.palette.text.secondary
        : conectado
            ? theme.palette.success.main
            : theme.palette.error.main;

    const descricaoStatus = conectado === undefined
        ? "Verificando conexão"
        : conectado
            ? "Conectado"
            : "Desconectado";

    const quantidadeComportamentos = integracao?.comportamentos?.length ?? 0;
    const quantidadeMensagens = integracao?.mensagensRapidas?.length ?? 0;
    const quantidadeNumeros = integracao?.numerosPermitidos?.length ?? 0;

    return (
        <CabecalhoPaper elevation={0}>
            <CapaContainer
                sx={{
                    background: `linear-gradient(120deg, ${alpha(VERDE_WHATSAPP, 0.85)}, ${alpha("#128C7E", 0.85)})`
                }}
            />
            <IdentificacaoContainer>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                    <Avatar
                        sx={{
                            width: 72,
                            height: 72,
                            mt: "-2.5rem",
                            backgroundColor: VERDE_WHATSAPP,
                            border: `3px solid ${theme.palette.background.paper}`,
                            boxShadow: theme.shadows[2]
                        }}
                    >
                        <WhatsApp sx={{ fontSize: "2.25rem" }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={600} noWrap>
                            {marketplace.nome}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    px: 1.25,
                                    py: 0.375,
                                    borderRadius: "999px",
                                    color: corStatus,
                                    backgroundColor: alpha(corStatus, 0.12)
                                }}
                            >
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: corStatus }} />
                                <Typography variant="caption" fontWeight={600}>
                                    {descricaoStatus}
                                </Typography>
                            </Box>
                            <Tooltip title={TipoMarketplaceDescricao[marketplace.tipo]}>
                                <Chip
                                    size="small"
                                    label={TipoMarketplaceTitulo[marketplace.tipo]}
                                    sx={{ background: TipoMarketplaceCor[marketplace.tipo], color: "white" }}
                                />
                            </Tooltip>
                            {marketplace.descricao && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {marketplace.descricao}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
                <AcoesContainer>
                    <Tooltip title="Abrir a central de conversas do atendimento">
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<OpenInNew />}
                            onClick={() => navigate("/pedidos/conversas")}
                        >
                            Atendimento
                        </Button>
                    </Tooltip>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={() => navigate(`/marketplace/editar/${marketplace.uuid}`)}
                    >
                        Editar dados
                    </Button>
                </AcoesContainer>
            </IdentificacaoContainer>
            <ResumoContainer>
                <ResumoItem>
                    <ResumoLabel>Instância</ResumoLabel>
                    <Typography variant="body2" fontWeight={600} noWrap>
                        {integracao?.instanciaId ?? "-"}
                    </Typography>
                </ResumoItem>
                <ResumoItem>
                    <ResumoLabel>Comportamentos</ResumoLabel>
                    <Typography variant="body2" fontWeight={600}>
                        {quantidadeComportamentos > 0 ? `${quantidadeComportamentos} ativo(s)` : "Nenhum"}
                    </Typography>
                </ResumoItem>
                <ResumoItem>
                    <ResumoLabel>Mensagens rápidas</ResumoLabel>
                    <Typography variant="body2" fontWeight={600}>
                        {quantidadeMensagens > 0 ? `${quantidadeMensagens} cadastrada(s)` : "Nenhuma"}
                    </Typography>
                </ResumoItem>
                <ResumoItem>
                    <ResumoLabel>Restrição de números</ResumoLabel>
                    <Typography variant="body2" fontWeight={600}>
                        {integracao?.restringirNumeros
                            ? `Ativa (${quantidadeNumeros})`
                            : "Inativa"}
                    </Typography>
                </ResumoItem>
            </ResumoContainer>
        </CabecalhoPaper>
    );
}
